import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import type {
  MessageVote,
  ChatHistory,
  AnnotationNote,
  Annotation,
  MakeCompactionEvalRequest,
  MakeCompactionEvalResponse,
  MakeStepReplayEvalRequest,
  MakeStepReplayEvalResponse,
} from '@/features/votes/message-votes';

import type { UseQueryOptions } from '@tanstack/react-query';

interface MessageVotesResponse {
  votes: MessageVote[];
  total: number;
}

export type VoteFilter = 'all' | 'upvote' | 'downvote';

export function useMessageVoteModels() {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['message-vote-models'],
    queryFn: async (): Promise<string[]> => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(`${API_URL}/admin/message-votes/models`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return [];
      }

      return await response.json();
    },
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

function buildMessageVoteQueryParams({
  filter,
  search,
  models,
  page,
  limit,
}: {
  filter: VoteFilter;
  search: string;
  models: string[];
  page?: number;
  limit?: number;
}) {
  const params = new URLSearchParams({ filter });

  if (page !== undefined) {
    params.set('page', page.toString());
  }
  if (limit !== undefined) {
    params.set('limit', limit.toString());
  }
  if (search) {
    params.append('search', search);
  }
  for (const model of models) {
    params.append('model', model);
  }

  return params;
}

export function useMessageVotes(
  page: number = 1,
  limit: number = 50,
  filter: VoteFilter = 'all',
  search: string = '',
  models: string[] = []
) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['message-votes', page, limit, filter, search, models],
    queryFn: async (): Promise<MessageVotesResponse> => {
      const token = await getAccessTokenSilently();
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const listParams = buildMessageVoteQueryParams({
        filter,
        search,
        models,
        page,
        limit,
      });
      const countParams = buildMessageVoteQueryParams({
        filter,
        search,
        models,
      });

      const response = await apiFetch(
        `${API_URL}/admin/message-votes?${listParams.toString()}`,
        { headers }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to fetch message votes'
        );
      }

      const votes = await response.json();

      const countResponse = await apiFetch(
        `${API_URL}/admin/message-votes/count?${countParams.toString()}`,
        { headers }
      );

      if (!countResponse.ok) {
        const errorData = await countResponse.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to fetch message votes count'
        );
      }

      const countData = await countResponse.json();

      return {
        votes,
        total: countData.total_count || 0,
      };
    },
    enabled: true,
  });
}

export function useResolveVote(
  chatId: string,
  messageId: string,
  userId: string
) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['resolve-vote', chatId, messageId, userId],
    queryFn: async (): Promise<number> => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        chat_id: chatId,
        message_id: messageId,
        user_id: userId,
      });

      const response = await apiFetch(
        `${API_URL}/admin/message-votes/resolve?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to resolve vote'
        );
      }

      return (await response.json()).vote_id;
    },
    enabled: !!chatId && !!messageId && !!userId,
  });
}

export function useChatHistory(
  voteId: number | null,
  options?: Pick<UseQueryOptions<ChatHistory, Error>, 'refetchInterval'>
) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['chat-history', voteId],
    refetchInterval: options?.refetchInterval,
    queryFn: async (): Promise<ChatHistory> => {
      if (!voteId) throw new Error('Vote ID is required');

      const token = await getAccessTokenSilently();

      const response = await apiFetch(
        `${API_URL}/admin/message-votes/${voteId}/chat-history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to fetch chat history'
        );
      }

      return await response.json();
    },
    enabled: !!voteId,
  });
}

export function useRunIntrospection(voteId: number) {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const token = await getAccessTokenSilently();

      const response = await apiFetch(
        `${API_URL}/admin/message-votes/${voteId}/run-introspection`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to queue introspection'
        );
      }
    },
  });
}

export function useMakeCompactionEval(voteId: number) {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async (
      request: MakeCompactionEvalRequest
    ): Promise<MakeCompactionEvalResponse> => {
      const token = await getAccessTokenSilently();

      const response = await apiFetch(
        `${API_URL}/admin/message-votes/${voteId}/compaction-eval`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to create the compaction eval case'
        );
      }

      return await response.json();
    },
  });
}

export function useMakeStepReplayEval(voteId: number) {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async (
      request: MakeStepReplayEvalRequest
    ): Promise<MakeStepReplayEvalResponse> => {
      const token = await getAccessTokenSilently();

      const response = await apiFetch(
        `${API_URL}/admin/message-votes/${voteId}/step-replay-eval`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to create the step replay eval case'
        );
      }

      return await response.json();
    },
  });
}

interface UpdateAnnotationRequest {
  entity_type: string;
  entity_id: number;
  status?: string | null;
  tags?: string[];
  notes?: AnnotationNote[];
}

export function useUpdateAnnotation(voteId: number) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAnnotationRequest): Promise<Annotation> => {
      const token = await getAccessTokenSilently();

      const response = await apiFetch(`${API_URL}/admin/annotations`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to update annotation'
        );
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-history', voteId] });
    },
  });
}
